import {
  Challenge,
  Goal,
  Widget,
  WidgetType,
} from 'features/chat/config/types/Message';

type WidgetCategory = 'challenge' | 'goal' | 'sprint' | 'journal' | 'generic';

type StatusVariant = 'success' | 'info' | 'danger' | 'neutral';

type WidgetMeta = {
  category: WidgetCategory;
  entityLabel: string;
  statusLabel: string;
  statusVariant: StatusVariant;
};

type BadgeTokens = {
  bg: string;
  color: string;
};

type Sprint = {
  id?: string;
  sprint_id?: string;
  title?: string;
  name?: string;
};

type WidgetPayload = Record<string, unknown> | undefined;

type JournalNote = {
  note_id?: string;
  journal_note_id?: string;
  title?: string;
  name?: string;
  subject?: string;
  summary?: string;
  excerpt?: string;
  preview?: string;
  description?: string;
  content?: string;
  body?: string;
  text?: string;
  updated_at?: string;
  updatedAt?: string;
  created_at?: string;
  createdAt?: string;
  [key: string]: unknown;
};

const WIDGET_TYPE_META: Record<string, WidgetMeta> = {
  challengeStatus: {
    category: 'challenge',
    entityLabel: 'Challenge',
    statusLabel: 'Status Updated',
    statusVariant: 'info',
  },
  challengeCreated: {
    category: 'challenge',
    entityLabel: 'Challenge',
    statusLabel: 'Created',
    statusVariant: 'success',
  },
  challengeEdited: {
    category: 'challenge',
    entityLabel: 'Challenge',
    statusLabel: 'Edited',
    statusVariant: 'info',
  },
  challengeRemoved: {
    category: 'challenge',
    entityLabel: 'Challenge',
    statusLabel: 'Removed',
    statusVariant: 'danger',
  },
  goalCreated: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Created',
    statusVariant: 'success',
  },
  goalEdited: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Edited',
    statusVariant: 'info',
  },
  goalDeleted: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Deleted',
    statusVariant: 'danger',
  },
  goalMoved: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Moved',
    statusVariant: 'info',
  },
  goalCompleted: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Completed',
    statusVariant: 'success',
  },
  goalPrioritized: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Prioritized',
    statusVariant: 'success',
  },
  goalUnprioritized: {
    category: 'goal',
    entityLabel: 'Goal',
    statusLabel: 'Unprioritized',
    statusVariant: 'info',
  },
  draftSprintStarted: {
    category: 'sprint',
    entityLabel: 'Draft Sprint',
    statusLabel: 'Started',
    statusVariant: 'success',
  },
  activeSprintFinished: {
    category: 'sprint',
    entityLabel: 'Active Sprint',
    statusLabel: 'Finished',
    statusVariant: 'success',
  },
  activeSprintRenamed: {
    category: 'sprint',
    entityLabel: 'Active Sprint',
    statusLabel: 'Renamed',
    statusVariant: 'info',
  },
  draftSprintRenamed: {
    category: 'sprint',
    entityLabel: 'Draft Sprint',
    statusLabel: 'Renamed',
    statusVariant: 'info',
  },
  activeSprintRestarted: {
    category: 'sprint',
    entityLabel: 'Active Sprint',
    statusLabel: 'Restarted',
    statusVariant: 'info',
  },
  journalNoteCreated: {
    category: 'journal',
    entityLabel: 'Journal',
    statusLabel: 'Created',
    statusVariant: 'success',
  },
  journalNoteEdited: {
    category: 'journal',
    entityLabel: 'Journal',
    statusLabel: 'Edited',
    statusVariant: 'info',
  },
  journalNoteDeleted: {
    category: 'journal',
    entityLabel: 'Journal',
    statusLabel: 'Deleted',
    statusVariant: 'danger',
  },
  journalNoteFound: {
    category: 'journal',
    entityLabel: 'Journal',
    statusLabel: 'Found',
    statusVariant: 'info',
  },
};

const BADGE_STYLES: Record<StatusVariant, BadgeTokens> = {
  success: {
    bg: 'rgba(42, 209, 98, 0.15)',
    color: 'var(--fgSuccess)',
  },
  info: {
    bg: 'rgba(108, 99, 255, 0.15)',
    color: 'var(--fgInteractive)',
  },
  danger: {
    bg: 'rgba(229, 74, 54, 0.15)',
    color: 'var(--fgDanger)',
  },
  neutral: {
    bg: 'rgba(255, 255, 255, 0.15)',
    color: 'var(--fgPrimary)',
  },
};

const DEFAULT_META: WidgetMeta = {
  category: 'generic',
  entityLabel: 'Update',
  statusLabel: 'Updated',
  statusVariant: 'neutral',
};

const getWidgetType = (widget: Widget): WidgetType | undefined => {
  const content = widget.content;

  if (!content) {
    return undefined;
  }

  const keys = Object.keys(content) as WidgetType[];

  for (const key of keys) {
    if (content[key] !== null && content[key] !== undefined) {
      return key;
    }
  }

  return undefined;
};

const toStartCase = (value: string) => {
  if (!value) {
    return '';
  }

  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getWidgetMeta = (widget: Widget): WidgetMeta => {
  const widgetType = getWidgetType(widget);

  if (!widgetType) {
    return DEFAULT_META;
  }

  const predefined = WIDGET_TYPE_META[widgetType];

  if (predefined) {
    return predefined;
  }

  if (widgetType.startsWith('journalNote')) {
    const status = widgetType.replace('journalNote', '');
    const statusVariant: StatusVariant = status.includes('deleted')
      ? 'danger'
      : status.includes('created')
      ? 'success'
      : 'info';

    return {
      category: 'journal',
      entityLabel: 'Journal',
      statusLabel: toStartCase(status),
      statusVariant,
    };
  }

  if (widgetType.startsWith('goal')) {
    return {
      category: 'goal',
      entityLabel: 'Goal',
      statusLabel: toStartCase(widgetType.replace('goal_', '')),
      statusVariant: 'info',
    };
  }

  if (widgetType.startsWith('challenge')) {
    return {
      category: 'challenge',
      entityLabel: 'Challenge',
      statusLabel: toStartCase(widgetType.replace('challenge', '')),
      statusVariant: 'info',
    };
  }

  if (widgetType.includes('sprint')) {
    const isDraft = widgetType.startsWith('draft');
    const status = widgetType.replace(isDraft ? 'draftSprint' : 'sprint', '');

    return {
      category: 'sprint',
      entityLabel: isDraft ? 'Draft Sprint' : 'Sprint',
      statusLabel: toStartCase(status),
      statusVariant: 'info',
    };
  }

  return {
    ...DEFAULT_META,
    statusLabel: toStartCase(widgetType) || DEFAULT_META.statusLabel,
  };
};

const getStatusBadgeTokens = (variant: StatusVariant): BadgeTokens => {
  return BADGE_STYLES[variant] ?? BADGE_STYLES.neutral;
};

const getFirstString = (
  payload: Record<string, unknown> | undefined,
  keys: string[]
): string | undefined => {
  if (!payload) {
    return undefined;
  }

  for (const key of keys) {
    const value = payload[key];

    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return undefined;
};

const getWidgetPayload = (widget: Widget): WidgetPayload => {
  const widgetType = getWidgetType(widget);

  if (!widgetType) {
    return undefined;
  }

  const payload = widget.content?.[widgetType];

  if (payload && typeof payload === 'object') {
    return payload as Record<string, unknown>;
  }

  return undefined;
};

const getChallengeFromWidget = (widget: Widget): Challenge | undefined => {
  const payload = getWidgetPayload(widget);
  const challenge = payload?.challenge;

  if (challenge && typeof challenge === 'object') {
    return challenge as Challenge;
  }

  return undefined;
};

const getGoalFromWidget = (widget: Widget): Goal | undefined => {
  const payload = getWidgetPayload(widget);
  const goal = payload?.goal;

  if (goal && typeof goal === 'object') {
    return goal as Goal;
  }

  return undefined;
};

const getSprintFromWidget = (widget: Widget): Sprint | undefined => {
  const payload = getWidgetPayload(widget);
  const sprint = payload?.sprint;

  if (sprint && typeof sprint === 'object') {
    return sprint as Sprint;
  }

  return undefined;
};

const coerceJournalNote = (candidate: unknown): JournalNote | undefined => {
  if (candidate && typeof candidate === 'object') {
    return candidate as JournalNote;
  }

  return undefined;
};

const getJournalNoteFromWidget = (widget: Widget): JournalNote | undefined => {
  const payload = getWidgetPayload(widget);

  if (payload) {
    const payloadNote =
      coerceJournalNote(payload.journalNote) ??
      coerceJournalNote(payload.note) ??
      coerceJournalNote(payload.journal_note);

    if (payloadNote) {
      return payloadNote;
    }
  }

  return undefined;
};

const getJournalNotePreview = (widget: Widget): string | undefined => {
  const note = getJournalNoteFromWidget(widget);
  const notePreview = getFirstString(note, [
    'excerpt',
    'preview',
    'summary',
    'description',
    'content',
    'body',
    'text',
  ]);

  if (notePreview) {
    return notePreview;
  }

  const payload = getWidgetPayload(widget);
  return getFirstString(payload, [
    'excerpt',
    'preview',
    'summary',
    'description',
    'content',
    'body',
    'text',
  ]);
};

const getJournalNoteTimestamp = (widget: Widget): string | undefined => {
  const note = getJournalNoteFromWidget(widget);
  const timestamp = getFirstString(note, [
    'updated_at',
    'updatedAt',
    'created_at',
    'createdAt',
    'date',
  ]);

  if (timestamp) {
    return timestamp;
  }

  const payload = getWidgetPayload(widget);
  return getFirstString(payload, [
    'updated_at',
    'updatedAt',
    'created_at',
    'createdAt',
    'date',
  ]);
};

const getWidgetTitle = (
  widget: Widget,
  meta: WidgetMeta
): string | undefined => {
  if (meta.category === 'challenge') {
    return getChallengeFromWidget(widget)?.title;
  }

  if (meta.category === 'goal') {
    return getGoalFromWidget(widget)?.title;
  }

  if (meta.category === 'journal') {
    const note = getJournalNoteFromWidget(widget);
    const noteTitle = getFirstString(note, ['title', 'name', 'subject']);

    if (noteTitle) {
      return noteTitle;
    }
  }

  if (meta.category === 'sprint') {
    const sprint = getSprintFromWidget(widget);

    if (sprint?.title) {
      return sprint.title;
    }

    if (sprint?.name) {
      return sprint.name;
    }
  }

  const payload = getWidgetPayload(widget);
  return getFirstString(payload, ['title', 'name', 'label']) ?? undefined;
};

const getChallengeFrequency = (widget: Widget): string | undefined => {
  const challenge = getChallengeFromWidget(widget);
  const target = challenge?.target;

  if (!target) {
    return undefined;
  }

  // Handle new weekday object format (e.g., { mon: true, tue: true, ... })
  if (target.weekday && typeof target.weekday === 'object') {
    const weekdayValues = Object.values(target.weekday);
    const daysCount = weekdayValues.filter(Boolean).length;

    if (daysCount === 7) {
      return 'Everyday';
    }

    if (daysCount > 0) {
      return `${daysCount} days/week`;
    }
  }

  // Handle old weekly array format
  if (Array.isArray(target.weekly) && target.weekly.length > 0) {
    const daysCount = target.weekly.filter(Boolean).length;

    if (daysCount === 7) {
      return 'Everyday';
    }

    if (daysCount > 0) {
      return `${daysCount} days/week`;
    }
  }

  if (target.type) {
    return toStartCase(target.type);
  }

  if (target.period) {
    return toStartCase(target.period);
  }

  return undefined;
};

const getWidgetSubtitle = (
  widget: Widget,
  meta: WidgetMeta
): string | undefined => {
  if (meta.category === 'challenge') {
    return getChallengeFrequency(widget);
  }

  if (meta.category === 'goal') {
    const goal = getGoalFromWidget(widget);

    if (goal?.period) {
      return toStartCase(goal.period);
    }
  }

  if (meta.category === 'journal') {
    return getJournalNoteTimestamp(widget);
  }

  if (meta.category === 'sprint') {
    const payload = getWidgetPayload(widget);
    return getFirstString(payload, ['date_range', 'dateRange', 'period']);
  }

  const payload = getWidgetPayload(widget);
  return getFirstString(payload, [
    'subtitle',
    'description',
    'details',
    'summary',
  ]);
};

const resolveStatusText = (widget: Widget, meta: WidgetMeta): string => {
  const widgetType = getWidgetType(widget);

  if (widgetType === 'challengeStatus') {
    const payload = getWidgetPayload(widget);
    const status = getFirstString(payload, [
      'status',
      'state',
      'label',
      'value',
    ]);

    if (status) {
      return toStartCase(status);
    }
  }

  if (widgetType === 'goalMoved') {
    const payload = getWidgetPayload(widget);
    const destination = getFirstString(payload, [
      'to_period',
      'toPeriod',
      'to',
      'destination',
    ]);

    if (destination) {
      return `Moved to ${toStartCase(destination)}`;
    }
  }

  if (widgetType === 'goalPrioritized') {
    const payload = getWidgetPayload(widget);
    const priority = getFirstString(payload, ['priority', 'rank']);

    if (priority) {
      return `Prioritized (${toStartCase(priority)})`;
    }
  }

  if (widgetType === 'goalUnprioritized') {
    const payload = getWidgetPayload(widget);
    const reason = getFirstString(payload, ['reason']);

    if (reason) {
      return `Unprioritized (${toStartCase(reason)})`;
    }
  }

  if (
    widgetType === 'activeSprintRenamed' ||
    widgetType === 'draftSprintRenamed'
  ) {
    const payload = getWidgetPayload(widget);
    const newName = getFirstString(payload, ['new_name', 'newName']);

    if (newName) {
      return `Renamed to ${newName}`;
    }
  }

  if (widgetType === 'journalNoteFound') {
    const payload = getWidgetPayload(widget);
    const query = getFirstString(payload, [
      'query',
      'search_term',
      'searchTerm',
    ]);

    if (query) {
      return `Found for "${query}"`;
    }

    const source = getFirstString(payload, ['source', 'origin']);

    if (source) {
      return `Found in ${toStartCase(source)}`;
    }
  }

  return meta.statusLabel;
};

export {
  getChallengeFromWidget,
  getGoalFromWidget,
  getSprintFromWidget,
  getJournalNoteFromWidget,
  getJournalNotePreview,
  getJournalNoteTimestamp,
  getWidgetMeta,
  getWidgetPayload,
  getWidgetSubtitle,
  getWidgetTitle,
  getWidgetType,
  getStatusBadgeTokens,
  resolveStatusText,
  toStartCase,
  getFirstString,
};

export type {
  BadgeTokens,
  StatusVariant,
  WidgetCategory,
  WidgetMeta,
  WidgetPayload,
  Sprint,
};
